import { useConfirm } from "../contexts/ConfirmContext";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getReq, postReq, putReq, deleteReq } from "../utils/request";
import { toast } from "react-toastify";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { useCanCreateOnPage } from "../hooks/usePagePermission";
import { useCommentsFeed, useEnsureEntityRoom, useRealtimeConnectTick } from "../contexts/RealtimeContext";
import { suggestActionsForText } from "../utils/commentSuggestions";
import { renderCommentContent, stripMentionMarkers } from "../utils/mentionUtils";
import { getTaskById } from "../services/taskService";
import { PageName } from "../types/Authorization";
import { ActivityDirection, ActivityItem, ActivityType } from "../types/Activity";
import { EntityParentType, Task, TaskAssignedUserRef } from "../types/Task";
import { dispatchCommentsChanged } from "./comments/commentEvents";
import CommentInput from "./CommentInput";
import Input from "./Input";
import TaskFormModal from "./Task/TaskFormModal";
import ActivityFormModal from "./ActivityFormModal";
import classes from "./commentSection.module.scss";
import Button from "./Button";
import AiResultCard from "./assistant/AiResultCard";

export interface CommentUser {
    userId: number;
    firstName: string;
    lastName: string | null;
    fullName: string;
    photo: string | null;
}

export interface CommentAck {
    userId: number;
    name: string;
    ackedAt: string;
}

export interface CommentItem {
    commentId: number;
    content: string;
    parentType?: string;
    parentId?: number;
    mentions: { userId: number; name: string }[] | null;
    isEdited: boolean;
    kind?: "text" | "location";
    latitude?: number | null;
    longitude?: number | null;
    replyTo?: { commentId: number; excerpt: string; authorName: string } | null;
    requiresAck?: boolean;
    isPinned?: boolean;
    /** Marked as a decision (B8). */
    isDecision?: boolean;
    decidedBy?: CommentUser | null;
    decidedAt?: string | null;
    actionType?: string | null;
    actionEntityType?: string | null;
    actionEntityId?: number | null;
    awaitingUser?: CommentUser | null;
    resolvedAt?: string | null;
    resolvedBy?: CommentUser | null;
    createdAt: string;
    updatedAt?: string;
    createdBy: CommentUser | null;
    acks?: CommentAck[];
    ackedByMe?: boolean;
    seenBy?: number;
    scopeLabel?: string | null;
    scopeLegId?: number | null;
}

interface Recipient {
    userId: number;
    name: string;
}

interface CommentSectionProps {
    parentType: string;
    parentId: number;
    rollupMissionId?: number;
}

/** BE caps a page at 100 — take the max so long threads open on the latest page. */
const PAGE_SIZE = 100;
/** Consecutive comments by the same author within this window share one header. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;
/** Debounce for the read receipt PUT. */
const READ_DEBOUNCE_MS = 1000;
const MAX_PREVIEW_NAMES = 3;
const ACTION_LABELS: Record<string, string> = {
    activity: "Activity logged",
    task: "Task",
    incident: "Incident",
    sitrep: "SitRep",
    service_request: "Service request",
    lead: "Lead",
};

// ─── Quick actions ───
// The thread only ever contains what people wrote. A quick action creates the
// object in its own module (activity / task / incident) and then either links
// it to the comment it started from, or prefills the composer with an editable
// note that carries the link. Nothing is posted automatically.
type QuickActionKey =
    | "log_call"
    | "schedule_meeting"
    | "schedule_follow_up"
    | "create_task"
    | "create_subtask"
    | "request_service"
    | "report_incident"
    | "copy_text";

interface QuickAction {
    key: QuickActionKey;
    label: string;
    hint: string;
    icon: string;
    /** Which module's CREATE permission the action needs (null = none). */
    needs: "activity" | "task" | null;
}

type ActionOrigin = { from: "composer" } | { from: "comment"; comment: CommentItem };

interface ActionLink {
    actionType: string;
    actionEntityType: string;
    actionEntityId: number;
}

interface TaskModalState {
    origin: ActionOrigin;
    initialData: Partial<Task>;
    parentType: EntityParentType | null;
    parentId: number | null;
    verb: "Task created" | "Service requested" | "Subtask created";
}

interface ActivityModalState {
    origin: ActionOrigin;
    initial: Partial<ActivityItem>;
    verb: "Call logged" | "Meeting scheduled" | "Follow-up scheduled";
}

const CRM_PARENTS = ["lead", "client", "company", "provider", "mission_request", "invoice", "bill", "payment"];
const MISSION_PARENTS = ["mission", "itinerary", "leg"];

const ACTIONS: Record<QuickActionKey, QuickAction> = {
    log_call: { key: "log_call", label: "Log call", icon: "bi-telephone", needs: "activity", hint: "Record a call on this record. The note stays yours to edit; the call is linked to it." },
    schedule_meeting: { key: "schedule_meeting", label: "Schedule meeting", icon: "bi-people", needs: "activity", hint: "Add a meeting activity on this record and link it here." },
    schedule_follow_up: { key: "schedule_follow_up", label: "Schedule follow-up", icon: "bi-calendar-check", needs: "activity", hint: "Log a call with a follow-up date (defaults to tomorrow 09:00) and link it here." },
    create_task: { key: "create_task", label: "Create task", icon: "bi-check2-square", needs: "task", hint: "Turn this into a task on this record. The task is linked to the comment." },
    create_subtask: { key: "create_subtask", label: "Create subtask", icon: "bi-diagram-3", needs: "task", hint: "Add a subtask under this task and link it here." },
    request_service: { key: "request_service", label: "Request service", icon: "bi-truck", needs: "task", hint: "Create a service-request task pre-assigned to the mission coordinator and link it here." },
    report_incident: { key: "report_incident", label: "Report incident", icon: "bi-exclamation-triangle", needs: null, hint: "Opens this leg's Incidents drawer. Incidents can't be prefilled — describe it there." },
    copy_text: { key: "copy_text", label: "Copy text", icon: "bi-clipboard", needs: null, hint: "Copy the plain text to the clipboard." },
};

/** Menu order per parent type (before permission filtering). */
const actionKeysFor = (parentType: string): QuickActionKey[] => {
    if (CRM_PARENTS.includes(parentType)) {
        return ["log_call", "schedule_meeting", "schedule_follow_up", "create_task", "copy_text"];
    }
    if (MISSION_PARENTS.includes(parentType)) {
        const keys: QuickActionKey[] = ["create_task", "request_service"];
        if (parentType === "leg") keys.push("report_incident");
        keys.push("copy_text");
        return keys;
    }
    if (parentType === "task") {
        return ["create_subtask", "log_call", "copy_text"];
    }
    return ["copy_text"];
};

const firstChars = (text: string, max: number): string => {
    const plain = (text || "").replace(/\s+/g, " ").trim();
    return plain.length > max ? plain.slice(0, max).trimEnd() : plain;
};

/** Tomorrow 09:00 local, formatted without a zone so the date never shifts. */
const tomorrowAtNine = (): string => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00:00`;
};

const sortComments = (list: CommentItem[]): CommentItem[] =>
    [...list].sort((a, b) => {
        const byTime = (a.createdAt || "").localeCompare(b.createdAt || "");
        return byTime !== 0 ? byTime : a.commentId - b.commentId;
    });

/** Merge incoming rows into the thread — same id replaces, order restored. */
const mergeComments = (prev: CommentItem[], incoming: CommentItem[]): CommentItem[] => {
    const byId = new Map<number, CommentItem>();
    prev.forEach((c) => byId.set(c.commentId, c));
    incoming.forEach((c) => byId.set(c.commentId, { ...(byId.get(c.commentId) ?? {}), ...c }));
    return sortComments(Array.from(byId.values()));
};

const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const dayLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = startOfDay(new Date());
    const day = startOfDay(date);
    const diffDays = Math.round((today - day) / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: sameYear ? undefined : "numeric" });
};

interface CommentRevision {
    revisionId: number;
    content: string;
    editedAt: string;
    editedBy: number | null;
    editedByName: string | null;
}

/**
 * One-tap replies: a click posts the text as-is (quoting the selected comment
 * if any). Mission-family threads get the radio prosigns the operators use;
 * every other record gets plain office acknowledgements.
 */
const MISSION_QUICK_REPLIES = ["ACK", "Copy", "Will do", "Negative", "Standby", "On station", "Moving", "Done"];
const OFFICE_QUICK_REPLIES = ["ACK", "Noted", "Will do", "Done", "Thanks"];
const MISSION_FAMILY_TYPES = new Set(["mission", "itinerary", "leg"]);

const timeLabel = (dateStr: string): string =>
    new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const getInitials = (user: CommentUser | null | undefined): string => {
    if (!user) return "?";
    return (user.firstName?.charAt(0) || "").toUpperCase() + (user.lastName?.charAt(0) || "").toUpperCase();
};

const excerptOf = (content: string, max: number = 90): string => {
    const plain = stripMentionMarkers(content || "").replace(/\s+/g, " ").trim();
    return plain.length > max ? plain.slice(0, max - 1) + "…" : plain;
};

const scopeLabelFor = (parentType: string): string => {
    if (parentType === "mission") return "Mission";
    if (parentType === "itinerary") return "Itinerary";
    if (parentType === "leg") return "Leg";
    return parentType;
};

const actionRoute = (actionType: string | null | undefined, entityId: number | null | undefined): string | null => {
    if (!entityId) return null;
    if (actionType === "task") return `/tasks/${entityId}`;
    if (actionType === "lead") return `/lead/${entityId}`;
    return null;
};

type ThreadRow =
    | { kind: "day"; key: string; label: string }
    | { kind: "new"; key: string }
    | { kind: "comment"; key: string; comment: CommentItem; showHeader: boolean };

/**
 * The nearest scrolling ancestor of `el` when it belongs to an overlay
 * (drawer, modal, dialog); null when the first scroller up the tree is the
 * page itself.
 */
const overlayScrollBox = (el: HTMLElement): HTMLElement | null => {
    let node = el.parentElement;
    while (node && node !== document.body) {
        const { overflowY } = window.getComputedStyle(node);
        if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
            return node.closest("[role='dialog'], .modal, .side-drawer") ? node : null;
        }
        node = node.parentElement;
    }
    return null;
};

const CommentSection: React.FC<CommentSectionProps> = ({ parentType, parentId, rollupMissionId }) => {
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [total, setTotal] = useState(0);
    const [pinned, setPinned] = useState<CommentItem | null>(null);
    const [watching, setWatching] = useState(false);
    /** Closed mission (completed / cancelled): the thread is archived — read, no posting. */
    const [readOnly, setReadOnly] = useState(false);
    const [lastReadAtOpen, setLastReadAtOpen] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasOlder, setHasOlder] = useState(false);
    const [oldestPage, setOldestPage] = useState(1);
    const [newComment, setNewComment] = useState("");
    const [replyTo, setReplyTo] = useState<CommentItem | null>(null);
    /** Which comment's "More" menu is open (one at a time). */
    const [openMenuFor, setOpenMenuFor] = useState<number | null>(null);
    /** Which comment's edit history is open, and the versions loaded for it. */
    const [historyFor, setHistoryFor] = useState<number | null>(null);
    const [history, setHistory] = useState<CommentRevision[] | null>(null);
    const toggleHistory = async (commentId: number) => {
        if (historyFor === commentId) {
            setHistoryFor(null);
            return;
        }
        setHistoryFor(commentId);
        setHistory(null);
        const response = await getReq(`comments/${commentId}/history`);
        setHistory(response.status === 200 && Array.isArray(response.body) ? response.body : []);
    };
    useEffect(() => {
        if (openMenuFor === null) return;
        const onDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target?.closest(`[data-comment-menu="${openMenuFor}"]`)) setOpenMenuFor(null);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenMenuFor(null); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [openMenuFor]);
    const [requiresAck, setRequiresAck] = useState(false);
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editContent, setEditContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [taskModal, setTaskModal] = useState<TaskModalState | null>(null);
    const [activityModal, setActivityModal] = useState<ActivityModalState | null>(null);
    const confirm = useConfirm();
    const { currentUser } = useUserPermissions();
    const currentUserId: number = currentUser?.userId || 0;
    const canCreateActivities = useCanCreateOnPage(PageName.ACTIVITIES);
    const canCreateTasks = useCanCreateOnPage(PageName.TASKS);
    const [searchParams, setSearchParams] = useSearchParams();

    const listRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const loadedRef = useRef(false);
    // Why the thread should scroll to its newest comment after the next render.
    const pendingScrollRef = useRef<"initial" | "post" | null>(null);
    const lastSeqRef = useRef(0);
    const connectTickSeenRef = useRef<number | null>(null);
    const commentsRef = useRef<CommentItem[]>([]);
    commentsRef.current = comments;

    const isActive = parentId > 0 || (rollupMissionId ?? 0) > 0;
    const feed = useCommentsFeed(parentType, parentId, rollupMissionId);
    const connectTick = useRealtimeConnectTick();
    useEnsureEntityRoom(parentId > 0 ? parentType : null, parentId);

    const threadUrl = useCallback((query: string) => (
        rollupMissionId
            ? `comments/mission-rollup/${rollupMissionId}?${query}`
            : `comments/${parentType}/${parentId}?${query}`
    ), [parentType, parentId, rollupMissionId]);

    const notifyChanged = useCallback(() => {
        dispatchCommentsChanged(parentType, parentId);
    }, [parentType, parentId]);

    /**
     * Brings the newest comment into view. On first load only the thread's
     * own scroll box (a drawer or modal body) moves — a thread inline on a
     * record page must not drag the page down to it. After your own post the
     * new comment is scrolled into view by the least movement.
     */
    const scrollToBottom = useCallback((reason: "initial" | "post") => {
        const el = bottomRef.current;
        if (!el) return;
        if (reason === "post") {
            if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
            return;
        }
        const box = overlayScrollBox(el);
        if (box) box.scrollTop = box.scrollHeight;
    }, []);

    const scrollToComment = useCallback((commentId: number) => {
        const el = listRef.current?.querySelector<HTMLElement>(`[data-comment-id="${commentId}"]`);
        if (!el) {
            toast.info("That comment isn't loaded — load older comments first.");
            return;
        }
        if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center" });
        el.classList.add(classes.flash);
        setTimeout(() => el.classList.remove(classes.flash), 1200);
    }, []);

    // ── Initial load: open on the LATEST page (BE pages oldest-first). ──
    const loadInitial = useCallback(async () => {
        if (!isActive) return;
        setIsLoading(true);
        const first = await getReq(threadUrl(`page=1&pageSize=${PAGE_SIZE}`));
        if (first.status !== 200 || !first.body) {
            setIsLoading(false);
            return;
        }
        let body = first.body;
        let pageNum = 1;
        const lastPage = Math.max(1, Math.ceil((body.total || 0) / PAGE_SIZE));
        if (lastPage > 1) {
            const last = await getReq(threadUrl(`page=${lastPage}&pageSize=${PAGE_SIZE}`));
            if (last.status === 200 && last.body) {
                body = last.body;
                pageNum = lastPage;
            }
        }
        setComments(sortComments(body.data || []));
        setTotal(body.total || 0);
        setPinned(body.pinned ?? null);
        setWatching(!!body.watching);
        setReadOnly(body.readOnly === true);
        setLastReadAtOpen(typeof body.myLastReadCommentId === "number" ? body.myLastReadCommentId : null);
        setOldestPage(pageNum);
        setHasOlder(pageNum > 1);
        loadedRef.current = true;
        pendingScrollRef.current = "initial";
        setIsLoading(false);
    }, [isActive, threadUrl]);

    const loadOlder = useCallback(async () => {
        const pageNum = oldestPage - 1;
        if (pageNum < 1 || isLoading) return;
        setIsLoading(true);
        const anchor = listRef.current?.querySelector<HTMLElement>("[data-comment-id]");
        const response = await getReq(threadUrl(`page=${pageNum}&pageSize=${PAGE_SIZE}`));
        if (response.status === 200 && response.body) {
            setComments((prev) => mergeComments(prev, response.body.data || []));
            setOldestPage(pageNum);
            setHasOlder(pageNum > 1);
            // Keep the reader's place: the row that was at the top stays in view.
            if (anchor && typeof anchor.scrollIntoView === "function") {
                setTimeout(() => anchor.scrollIntoView({ block: "start" }), 0);
            }
        }
        setIsLoading(false);
    }, [oldestPage, isLoading, threadUrl]);

    /** Reconnect catch-up — only what arrived after the newest known comment. */
    const catchUp = useCallback(async () => {
        if (!loadedRef.current) return;
        const known = commentsRef.current;
        const lastId = known.length ? Math.max(...known.map((c) => c.commentId)) : 0;
        if (!lastId) {
            loadInitial();
            return;
        }
        const response = await getReq(threadUrl(`after=${lastId}&page=1&pageSize=${PAGE_SIZE}`));
        if (response.status === 200 && response.body && Array.isArray(response.body.data) && response.body.data.length) {
            const fresh: CommentItem[] = response.body.data;
            setComments((prev) => {
                const unknown = fresh.filter((c) => !prev.some((p) => p.commentId === c.commentId)).length;
                if (unknown) setTotal((t) => t + unknown);
                return mergeComments(prev, fresh);
            });
            notifyChanged();
        }
    }, [threadUrl, loadInitial, notifyChanged]);

    const loadRecipients = useCallback(async () => {
        if (!(parentId > 0)) return;
        const response = await getReq(`comments/recipients-preview/${parentType}/${parentId}`);
        if (response.status === 200 && response.body && Array.isArray(response.body.users)) {
            setRecipients(response.body.users);
        }
    }, [parentType, parentId]);

    useEffect(() => {
        loadedRef.current = false;
        lastSeqRef.current = 0;
        setComments([]);
        setPinned(null);
        setReadOnly(false);
        setReplyTo(null);
        setEditingId(null);
        loadInitial();
        loadRecipients();
    }, [loadInitial, loadRecipients]);

    // Auto-scroll after the thread first renders and after own posts.
    useEffect(() => {
        const reason = pendingScrollRef.current;
        if (!reason || isLoading) return;
        pendingScrollRef.current = null;
        scrollToBottom(reason);
    }, [comments, isLoading, scrollToBottom]);

    // ── Realtime: apply add / replace / remove from the socket feed. ──
    useEffect(() => {
        const fresh = feed.filter((e) => e.seq > lastSeqRef.current).sort((a, b) => a.seq - b.seq);
        if (!fresh.length) return;
        lastSeqRef.current = fresh[fresh.length - 1].seq;
        if (!loadedRef.current) return;
        let delta = 0;
        setComments((prev) => {
            let next = prev;
            for (const event of fresh) {
                const incoming = event.comment as CommentItem;
                const exists = next.some((c) => c.commentId === incoming.commentId);
                if (event.type === "deleted") {
                    if (exists) {
                        next = next.filter((c) => c.commentId !== incoming.commentId);
                        delta -= 1;
                    }
                } else if (event.type === "updated") {
                    if (exists) next = mergeComments(next, [incoming]);
                } else if (!exists) {
                    const row = rollupMissionId && !incoming.scopeLabel
                        ? { ...incoming, scopeLabel: scopeLabelFor(event.parentType) }
                        : incoming;
                    next = mergeComments(next, [row]);
                    delta += 1;
                }
            }
            return next;
        });
        if (delta !== 0) setTotal((t) => Math.max(0, t + delta));
        setPinned((prev) => {
            let next = prev;
            for (const event of fresh) {
                const incoming = event.comment as CommentItem;
                if (event.type === "deleted") {
                    if (next?.commentId === incoming.commentId) next = null;
                } else if (incoming.isPinned) {
                    next = incoming;
                } else if (next?.commentId === incoming.commentId) {
                    next = null;
                }
            }
            return next;
        });
        notifyChanged();
    }, [feed, rollupMissionId, notifyChanged]);

    // ── Reconnect: catch up with ?after= (skip the tick present at mount). ──
    useEffect(() => {
        if (connectTickSeenRef.current === null) {
            connectTickSeenRef.current = connectTick;
            return;
        }
        if (connectTick !== connectTickSeenRef.current) {
            connectTickSeenRef.current = connectTick;
            catchUp();
        }
    }, [connectTick, catchUp]);

    // ── Read receipt: on open and whenever the newest comment changes. ──
    const newestId = comments.length ? comments[comments.length - 1].commentId : 0;
    useEffect(() => {
        if (!(parentId > 0) || !loadedRef.current) return;
        const timer = setTimeout(async () => {
            const response = await putReq(`comments/read/${parentType}/${parentId}`, {});
            if (response.status === 200) notifyChanged();
        }, READ_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [newestId, parentType, parentId, notifyChanged]);

    // ── Composer ──
    const handleSubmit = () => submitContent(newComment.trim());

    /** Post `content` on this thread (reply / ACK flags from the composer apply). */
    const submitContent = async (content: string) => {
        if (!content || isSubmitting) return;
        setIsSubmitting(true);
        const payload: Record<string, unknown> = {
            parentType,
            parentId,
            content,
        };
        if (replyTo) {
            payload.replyToCommentId = replyTo.commentId;
            // In the mission thread a reply to a leg / itinerary comment belongs to
            // that record, not the mission — the server refuses cross-record replies.
            if (replyTo.parentType && (replyTo.parentType !== parentType || Number(replyTo.parentId) !== Number(parentId))) {
                payload.parentType = replyTo.parentType;
                payload.parentId = replyTo.parentId;
            }
        }
        if (requiresAck) payload.requiresAck = true;
        const response = await postReq("comments", payload);
        if (response.status === 200 || response.status === 201) {
            setNewComment("");
            setReplyTo(null);
            setRequiresAck(false);
            const created = response.body as CommentItem;
            if (created && created.commentId) {
                if (rollupMissionId && !created.scopeLabel) created.scopeLabel = scopeLabelFor(parentType);
                // The socket echo may have landed first — count it only once.
                if (!commentsRef.current.some((c) => c.commentId === created.commentId)) {
                    setTotal((t) => t + 1);
                }
                setComments((prev) => mergeComments(prev, [created]));
                pendingScrollRef.current = "post";
            } else {
                loadInitial();
            }
            notifyChanged();
            loadRecipients();
        } else if (response.status === 429) {
            toast.error("Too many comments in a minute — wait a moment and try again.");
        } else {
            toast.error(response.body?.message || "Failed to post comment");
        }
        setIsSubmitting(false);
    };

    const handleUpdate = async (commentId: number) => {
        if (!editContent.trim() || isSubmitting) return;
        setIsSubmitting(true);
        const response = await putReq(`comments/${commentId}`, { content: editContent.trim() });
        if (response.status === 200) {
            setEditingId(null);
            setEditContent("");
            const updated = response.body as CommentItem;
            if (updated && updated.commentId) {
                setComments((prev) => mergeComments(prev, [updated]));
            } else {
                loadInitial();
            }
        } else if (response.status === 403) {
            toast.error(response.body?.message || "You can no longer edit this comment.");
        } else {
            toast.error(response.body?.message || "Failed to update comment");
        }
        setIsSubmitting(false);
    };

    const handleDelete = async (commentId: number) => {
        const confirmed = await confirm({
            danger: true,
            entity: "Comment",
            titleVerb: "Delete",
            message: "Delete this comment? This cannot be undone.",
            confirmLabel: "Delete",
            cancelLabel: "Keep",
        });
        if (!confirmed) return;

        const response = await deleteReq(`comments/${commentId}`);
        if (response.status === 200) {
            setComments((prev) => prev.filter((c) => c.commentId !== commentId));
            setTotal((prev) => Math.max(0, prev - 1));
            setPinned((prev) => (prev?.commentId === commentId ? null : prev));
            if (replyTo?.commentId === commentId) setReplyTo(null);
            notifyChanged();
        } else {
            toast.error(response.body?.message || "Failed to delete comment");
        }
    };

    // The thread's decisions, every page of it (B8).
    const [decisions, setDecisions] = useState<Array<{ commentId: number; content: string; decidedAt: string | null; decidedBy: string | null }>>([]);
    const [showDecisions, setShowDecisions] = useState(false);
    const decisionKey = comments.filter((c) => c.isDecision).map((c) => c.commentId).join(",");
    useEffect(() => {
        if (!(parentId > 0)) { setDecisions([]); return; }
        let alive = true;
        Promise.resolve(getReq(`comments/decisions/${parentType}/${parentId}`))
            .then((response: any) => { if (alive) setDecisions(response?.status === 200 && Array.isArray(response.body) ? response.body : []); })
            .catch(() => undefined);
        return () => { alive = false; };
    }, [parentType, parentId, decisionKey]);

    const handleDecision = async (comment: CommentItem, decision: boolean) => {
        setBusyId(comment.commentId);
        const response = await putReq(`comments/${comment.commentId}/decision`, { decision });
        if (response.status === 200) applyServerComment(response.body as CommentItem);
        else toast.error(response.body?.message || (decision ? "Could not mark it as a decision" : "Could not unmark it"));
        setBusyId(null);
    };

    /** Apply a server-returned comment (ack / pin / resolve / action responses). */
    const applyServerComment = (updated: CommentItem) => {
        if (!updated || !updated.commentId) return;
        setComments((prev) => mergeComments(prev, [updated]));
        setPinned((prev) => {
            if (updated.isPinned) return updated;
            return prev?.commentId === updated.commentId ? null : prev;
        });
        notifyChanged();
    };

    const handleAck = async (comment: CommentItem) => {
        setBusyId(comment.commentId);
        const response = await postReq(`comments/${comment.commentId}/ack`, {});
        if (response.status === 200) {
            applyServerComment(response.body as CommentItem);
        } else {
            toast.error(response.body?.message || "Could not acknowledge");
        }
        setBusyId(null);
    };

    const handlePin = async (comment: CommentItem, pinnedNext: boolean) => {
        setBusyId(comment.commentId);
        const response = await putReq(`comments/${comment.commentId}/pin`, { pinned: pinnedNext });
        if (response.status === 200) {
            const updated = response.body as CommentItem;
            // Only one pin per thread — the server unpins the previous one.
            if (pinnedNext) {
                setComments((prev) => prev.map((c) => (c.commentId !== updated.commentId && c.isPinned ? { ...c, isPinned: false } : c)));
            }
            applyServerComment(updated);
        } else {
            toast.error(response.body?.message || (pinnedNext ? "Could not pin" : "Could not unpin"));
        }
        setBusyId(null);
    };

    const handleResolve = async (comment: CommentItem, resolved: boolean) => {
        setBusyId(comment.commentId);
        const response = await putReq(`comments/${comment.commentId}/resolve`, { resolved });
        if (response.status === 200) {
            applyServerComment(response.body as CommentItem);
        } else {
            toast.error(response.body?.message || (resolved ? "Could not resolve" : "Could not reopen"));
        }
        setBusyId(null);
    };

    const handleWatch = async () => {
        const next = !watching;
        setWatching(next);
        const response = await putReq(`comments/watch/${parentType}/${parentId}`, { watching: next });
        if (response.status === 200) {
            setWatching(!!response.body?.watching);
        } else {
            setWatching(!next);
            toast.error(response.body?.message || "Could not update watch");
        }
    };

    const startEditing = (comment: CommentItem) => {
        setEditingId(comment.commentId);
        setEditContent(comment.content);
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditContent("");
    };

    // ── Quick actions ──
    const availableActions = useMemo<QuickAction[]>(() => (
        actionKeysFor(parentType)
            .map((key) => ACTIONS[key])
            .filter((a) => a.needs === null || (a.needs === "activity" ? canCreateActivities : canCreateTasks))
    ), [parentType, canCreateActivities, canCreateTasks]);

    const isActionAvailable = useCallback(
        (key: string) => availableActions.some((a) => a.key === key),
        [availableActions],
    );

    /** Same rules over a posted comment; hidden once the comment already links an action. */
    const renderTextSuggestions = (comment: CommentItem) => {
        if (comment.actionType) return null;
        const keys = suggestActionsForText(comment.content, parentType).filter((k) => isActionAvailable(k));
        if (keys.length === 0) return null;
        return (
            <div className={classes.textSuggestions} data-testid="comment-suggestions">
                <span className={classes.suggestedLabel} title="Suggested from the words in this comment — optional shortcuts, nothing is posted for you.">Suggested</span>
                {keys.map((key) => {
                    const action = ACTIONS[key as QuickActionKey];
                    if (!action) return null;
                    return (
                        <Button
                            key={key}
                            type="button"
                            size="xs"
                            title={action.hint}
                            onClick={() => runAction(key, { from: "comment", comment })}
                        >
                            <i className={`bi ${action.icon}`} /> {action.label}
                        </Button>
                    );
                })}
            </div>
        );
    };

    const originText = (origin: ActionOrigin): string =>
        stripMentionMarkers(origin.from === "comment" ? origin.comment.content : newComment).replace(/\s+/g, " ").trim();

    /** Stamp the link onto the source comment — the comment then renders a "→ …" chip. */
    const finishAction = async (origin: ActionOrigin, link: ActionLink, _label: string, _note: string) => {
        if (origin.from !== "comment") return;
        const response = await putReq(`comments/${origin.comment.commentId}/action`, link);
        if (response.status === 200) {
            applyServerComment(response.body as CommentItem);
        } else {
            toast.error(response.body?.message || "Created, but could not link it to the comment.");
        }
    };

    const findMissionAssignee = async (): Promise<TaskAssignedUserRef | null> => {
        const response = await getReq(`comments/mentionable/${parentType}/${parentId}`);
        if (response.status !== 200 || !Array.isArray(response.body)) return null;
        const assignee = response.body.find((r: any) => r.role === "assignee");
        if (!assignee) return null;
        return {
            userId: assignee.userId,
            firstName: assignee.firstName,
            lastName: assignee.lastName ?? null,
            fullName: [assignee.firstName, assignee.lastName].filter(Boolean).join(" "),
            photo: null,
        };
    };

    const openActivityAction = (key: QuickActionKey, origin: ActionOrigin) => {
        const text = originText(origin);
        const base: Partial<ActivityItem> = {
            direction: ActivityDirection.Outbound,
            subject: firstChars(text, 80),
            description: text || null,
        };
        if (key === "schedule_meeting") {
            setActivityModal({ origin, verb: "Meeting scheduled", initial: { ...base, activityType: ActivityType.Meeting } });
        } else if (key === "schedule_follow_up") {
            setActivityModal({ origin, verb: "Follow-up scheduled", initial: { ...base, activityType: ActivityType.Call, nextFollowUpDate: tomorrowAtNine() } });
        } else {
            setActivityModal({ origin, verb: "Call logged", initial: { ...base, activityType: ActivityType.Call } });
        }
    };

    const openTaskAction = async (key: QuickActionKey, origin: ActionOrigin) => {
        const text = originText(origin);
        if (key === "request_service") {
            const assignee = await findMissionAssignee();
            setTaskModal({
                origin,
                verb: "Service requested",
                parentType: parentType as EntityParentType,
                parentId,
                initialData: { name: `Service request: ${firstChars(text, 60)}`.trim(), description: text, assignedUser: assignee },
            });
            return;
        }
        if (key === "create_subtask") {
            // A subtask hangs off the task's own parent record; look it up so the
            // modal lands it in the right place. Falls back to a plain nested task.
            const response = await getTaskById(parentId);
            const parent: Task | null = response?.status === 200 && response.body ? (response.body?.data ?? response.body) : null;
            setTaskModal({
                origin,
                verb: "Subtask created",
                parentType: parent?.parentType ?? null,
                parentId: parent?.parentId ?? null,
                initialData: { name: firstChars(text, 80), description: text, parentTaskId: parentId },
            });
            return;
        }
        setTaskModal({
            origin,
            verb: "Task created",
            parentType: parentType as EntityParentType,
            parentId,
            initialData: { name: firstChars(text, 80), description: text },
        });
    };

    const reportIncident = () => {
        // The leg's Incidents drawer is URL-addressed; keep mission/itinerary/leg params.
        const next = new URLSearchParams(searchParams);
        next.set("legDrawer", "incidents");
        setSearchParams(next, { replace: true });
        toast.info("Describe the incident in the Incidents drawer.");
    };

    const copyText = async (origin: ActionOrigin) => {
        const text = originText(origin);
        if (!text) {
            toast.info("Nothing to copy yet.");
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied");
        } catch {
            toast.error("Could not copy — select the text and copy it manually.");
        }
    };

    const runAction = (key: string, origin: ActionOrigin) => {
        if (!isActionAvailable(key)) return;
        switch (key as QuickActionKey) {
            case "log_call":
            case "schedule_meeting":
            case "schedule_follow_up":
                openActivityAction(key as QuickActionKey, origin);
                break;
            case "create_task":
            case "create_subtask":
            case "request_service":
                openTaskAction(key as QuickActionKey, origin);
                break;
            case "report_incident":
                reportIncident();
                break;
            case "copy_text":
                copyText(origin);
                break;
            default:
                break;
        }
    };

    const handleActivitySaved = (activity: ActivityItem) => {
        if (!activityModal || activity.activityId == null) return;
        const link: ActionLink = { actionType: "activity", actionEntityType: "activity", actionEntityId: activity.activityId };
        finishAction(activityModal.origin, link, activityModal.verb, `${activityModal.verb}: ${activity.subject}`);
    };

    const handleTaskSaved = (task: Task) => {
        if (!taskModal || !task.taskId) return;
        const link: ActionLink = { actionType: "task", actionEntityType: "task", actionEntityId: task.taskId };
        finishAction(taskModal.origin, link, `Task #${task.taskId}`, `${taskModal.verb}: ${task.name ?? ""}`.trim());
    };

    const renderActionMenu = (origin: ActionOrigin, actions: QuickAction[], toggleLabel: string, toggleIcon: string, toggleTitle: string) => {
        const menuKey = origin.from === "comment" ? origin.comment.commentId : 0;
        const isOpen = openMenuFor === menuKey;
        return (
            <span className={classes.actionMenuWrap} data-comment-menu={menuKey}>
                <Button
                    type="button"
                    size="xs"
                    title={toggleTitle}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    onClick={() => setOpenMenuFor(isOpen ? null : menuKey)}
                >
                    <i className={`bi ${toggleIcon}`} />
                    {toggleLabel}
                </Button>
                {isOpen && (
                    <div className={classes.actionMenu} role="menu">
                        {actions.map((action) => (
                            <button
                                key={action.key}
                                type="button"
                                role="menuitem"
                                className={`btn-unstyled ${classes.actionMenuItem}`}
                                title={action.hint}
                                onClick={() => { setOpenMenuFor(null); runAction(action.key, origin); }}
                            >
                                <i className={`bi ${action.icon}`} />
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </span>
        );
    };

    // ── Rows: day separators, "new" marker, author grouping ──
    const rows = useMemo<ThreadRow[]>(() => {
        const out: ThreadRow[] = [];
        let lastDay: number | null = null;
        let prev: CommentItem | null = null;
        let newMarkerPlaced = false;
        comments.forEach((comment) => {
            const created = new Date(comment.createdAt);
            const day = startOfDay(created);
            if (lastDay !== day) {
                out.push({ kind: "day", key: `day-${day}`, label: dayLabel(comment.createdAt) });
                lastDay = day;
                prev = null;
            }
            if (
                !newMarkerPlaced
                && lastReadAtOpen !== null
                && comment.commentId > lastReadAtOpen
                && comment.createdBy?.userId !== currentUserId
            ) {
                out.push({ kind: "new", key: "new-marker" });
                newMarkerPlaced = true;
                prev = null;
            }
            const grouped = !!prev
                && !!prev.createdBy && !!comment.createdBy
                && prev.createdBy.userId === comment.createdBy.userId
                && created.getTime() - new Date(prev.createdAt).getTime() < GROUP_WINDOW_MS
                && !comment.requiresAck && !prev.requiresAck
                && !comment.replyTo
                // A shared position always shows who shared it.
                && comment.kind !== "location";
            out.push({ kind: "comment", key: `c-${comment.commentId}`, comment, showHeader: !grouped });
            prev = comment;
        });
        return out;
    }, [comments, lastReadAtOpen, currentUserId]);

    // How many comments from others arrived since the person last read the thread.
    const newSinceCount = lastReadAtOpen === null ? 0
        : comments.filter((c) => c.commentId > lastReadAtOpen && c.createdBy?.userId !== currentUserId).length;

    const recipientsText = useMemo(() => {
        const others = recipients.filter((r) => r.userId !== currentUserId);
        if (!others.length) return "Only you will see this";
        const names = others.slice(0, MAX_PREVIEW_NAMES).map((r) => r.name).join(", ");
        const more = others.length - MAX_PREVIEW_NAMES;
        return more > 0 ? `Will notify: ${names} + ${more} more` : `Will notify: ${names}`;
    }, [recipients, currentUserId]);

    const renderAckMatrix = (comment: CommentItem) => {
        const acks = comment.acks ?? [];
        const authorId = comment.createdBy?.userId;
        const pending = recipients.filter((r) => r.userId !== authorId && !acks.some((a) => a.userId === r.userId));
        const expected = acks.length + pending.length;
        return (
            <div className={classes.ackMatrix} title="Who has acknowledged this comment.">
                <span className={classes.ackCount}>{acks.length} of {expected} acked</span>
                {acks.map((a) => (
                    <span key={`a-${a.userId}`} className={classes.ackName} title={new Date(a.ackedAt).toLocaleString()}>{a.name} ✓</span>
                ))}
                {pending.map((p) => (
                    <span key={`p-${p.userId}`} className={classes.ackPending}>{p.name} pending</span>
                ))}
            </div>
        );
    };

    const renderChips = (comment: CommentItem) => {
        const hasLocation = comment.kind === "location" && comment.latitude != null && comment.longitude != null;
        const actionLabel = comment.actionType ? (ACTION_LABELS[comment.actionType] || comment.actionType) : null;
        const route = actionRoute(comment.actionType, comment.actionEntityId);
        if (!hasLocation && !actionLabel) return null;
        return (
            <div className={classes.chipRow}>
                {hasLocation && (
                    <a
                        className={classes.locationCard}
                        href={`https://maps.google.com/?q=${comment.latitude},${comment.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open this position in Google Maps"
                    >
                        <span className={classes.locationIcon}><i className="bi bi-geo-alt-fill" /></span>
                        <span className={classes.locationText}>
                            <span className={classes.locationTitle}>Shared position</span>
                            <span className={classes.locationCoords}>{Number(comment.latitude).toFixed(4)}, {Number(comment.longitude).toFixed(4)} · open map</span>
                        </span>
                    </a>
                )}
                {actionLabel && (
                    route ? (
                        <Link className={classes.actionChip} to={route} title={`Open ${actionLabel.toLowerCase()} #${comment.actionEntityId}`}>
                            → {actionLabel} #{comment.actionEntityId}
                        </Link>
                    ) : (
                        <span className={classes.actionChip} title="Created from this comment.">→ {actionLabel}</span>
                    )
                )}
            </div>
        );
    };

    const renderComment = (comment: CommentItem, showHeader: boolean) => {
        const isMine = comment.createdBy?.userId === currentUserId;
        const isEditing = editingId === comment.commentId;
        const isBusy = busyId === comment.commentId;
        const isResolved = !!comment.resolvedAt;
        const showAckButton = !!comment.requiresAck && !comment.ackedByMe && !isMine;
        const itemClass = [
            classes.commentItem,
            showHeader ? "" : classes.commentItemGrouped,
            comment.requiresAck ? classes.ackRequired : "",
            isResolved ? classes.resolved : "",
        ].filter(Boolean).join(" ");
        return (
            <div key={comment.commentId} className={itemClass} data-comment-id={comment.commentId} data-testid="comment-row">
                <div className={classes.commentAvatar}>
                    {showHeader && <div className={classes.avatarInitials}>{getInitials(comment.createdBy)}</div>}
                </div>
                <div className={classes.commentBody}>
                    <div className={classes.commentMeta}>
                        {showHeader && (
                            <span className={classes.commentAuthor}>{comment.createdBy?.fullName || "Unknown"}</span>
                        )}
                        <span className={classes.commentDate} title={new Date(comment.createdAt).toLocaleString()}>
                            {timeLabel(comment.createdAt)}
                        </span>
                        {comment.isEdited && (
                            <button
                                type="button"
                                className={`btn-unstyled ${classes.editedLabel}`}
                                title={`Edited ${comment.updatedAt ? new Date(comment.updatedAt).toLocaleString() : ""} — see what it said before`}
                                aria-expanded={historyFor === comment.commentId}
                                onClick={() => toggleHistory(comment.commentId)}
                            >
                                edited{comment.updatedAt ? ` · ${timeLabel(comment.updatedAt)}` : ""}
                            </button>
                        )}
                        {comment.isPinned && <span className={classes.pinnedTag} title="Pinned to the top of this thread.">pinned</span>}
                        {comment.isDecision && (
                            <span className={classes.decisionTag} title={`Marked as a decision${comment.decidedBy?.fullName ? ` by ${comment.decidedBy.fullName}` : ""}${comment.decidedAt ? ` · ${new Date(comment.decidedAt).toLocaleString()}` : ""}`}>
                                <i className="bi bi-patch-check-fill" aria-hidden="true" /> decision
                            </span>
                        )}
                        {comment.scopeLabel && (
                            <span className={classes.scopeChip} title="Where this comment was posted.">{comment.scopeLabel}</span>
                        )}
                        {!isEditing && (
                            <div className={classes.commentActions}>
                                {!readOnly && (
                                    <Button type="button" size="xs" icon="reply" title="Quote this comment in your reply" onClick={() => setReplyTo(comment)}>
                                        Reply
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="xs"
                                    disabled={isBusy}
                                    title={comment.isPinned ? "Remove this comment from the top of the thread" : "Keep this comment at the top of the thread"}
                                    onClick={() => handlePin(comment, !comment.isPinned)}
                                >
                                    <i className={`bi ${comment.isPinned ? "bi-pin-angle-fill" : "bi-pin-angle"}`} />
                                    {comment.isPinned ? "Unpin" : "Pin"}
                                </Button>
                                {comment.kind !== "location" && (
                                    <Button
                                        type="button"
                                        size="xs"
                                        disabled={isBusy}
                                        title={comment.isDecision ? "It isn't a decision after all — take it off the decisions list" : "Mark this as a decision — it goes on the thread's decisions list"}
                                        onClick={() => handleDecision(comment, !comment.isDecision)}
                                    >
                                        <i className={`bi ${comment.isDecision ? "bi-patch-check-fill" : "bi-patch-check"}`} />
                                        {comment.isDecision ? "Not a decision" : "Decision"}
                                    </Button>
                                )}
                                {(comment.awaitingUser || isResolved) && (
                                    <Button
                                        type="button"
                                        size="xs"
                                        disabled={isBusy}
                                        title={isResolved ? "Reopen — this still needs attention" : "Mark this as handled"}
                                        onClick={() => handleResolve(comment, !isResolved)}
                                    >
                                        <i className={`bi ${isResolved ? "bi-arrow-counterclockwise" : "bi-check2-circle"}`} />
                                        {isResolved ? "Reopen" : "Resolve"}
                                    </Button>
                                )}
                                {isMine && (
                                    <>
                                        <Button type="button" size="xs" icon="pencil" title="Edit your comment" onClick={() => startEditing(comment)}>
                                            Edit
                                        </Button>
                                        <Button type="button" size="xs" variant="danger" icon="trash" title="Delete your comment" onClick={() => handleDelete(comment.commentId)}>
                                            Delete
                                        </Button>
                                    </>
                                )}
                                {availableActions.length > 0 && renderActionMenu(
                                    { from: "comment", comment },
                                    availableActions,
                                    "More ▾",
                                    "bi-three-dots",
                                    "Turn this comment into a call, task or other record — it gets linked here.",
                                )}
                            </div>
                        )}
                    </div>
                    {comment.replyTo && (
                        <button
                            type="button"
                            className={`btn-unstyled ${classes.replyQuote}`}
                            title="Jump to the original comment"
                            onClick={() => scrollToComment(comment.replyTo!.commentId)}
                        >
                            <span className={classes.replyQuoteAuthor}>{comment.replyTo.authorName}</span>
                            <span className={classes.replyQuoteText}>{comment.replyTo.excerpt}</span>
                        </button>
                    )}
                    {renderChips(comment)}
                    {isEditing ? (
                        <div className={classes.editForm}>
                            <CommentInput
                                value={editContent}
                                onChange={setEditContent}
                                onSubmit={() => handleUpdate(comment.commentId)}
                                mentionScope={parentId > 0 ? { parentType, parentId } : undefined}
                                autoFocus
                            />
                            <div className={classes.editActions}>
                                <Button type="button" size="xs" icon="check2" title="Save your changes to this comment" onClick={() => handleUpdate(comment.commentId)} disabled={isSubmitting}>
                                    Save
                                </Button>
                                <Button type="button" size="xs" icon="x-lg" title="Discard the changes" onClick={cancelEditing}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        !(comment.kind === "location" && /^(📍\s*)?position$/i.test(stripMentionMarkers(comment.content).trim())) && (
                            <div className={classes.commentContent}>
                                {renderCommentContent(comment.content, { mentionClassName: classes.mentionTag, referenceClassName: classes.referenceLink })}
                            </div>
                        )
                    )}
                    {historyFor === comment.commentId && (
                        <div className={classes.historyPanel} data-testid="comment-history">
                            <div className={classes.historyTitle}>Previous versions</div>
                            {history === null && <div className={classes.historyMeta}>Loading…</div>}
                            {history !== null && history.length === 0 && <div className={classes.historyMeta}>No earlier version kept.</div>}
                            {(history ?? []).map((rev) => (
                                <div key={rev.revisionId} className={classes.historyItem}>
                                    <div className={classes.historyMeta}>
                                        {rev.editedByName || "Someone"} changed it {new Date(rev.editedAt).toLocaleString()}
                                    </div>
                                    <div className={classes.historyText}>{stripMentionMarkers(rev.content)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {renderTextSuggestions(comment)}
                    {comment.requiresAck && (
                        <div className={classes.ackRow}>
                            {showAckButton && (
                                <Button
                                    type="button"
                                    size="xs"
                                    variant="primary"
                                    icon="check2"
                                    disabled={isBusy}
                                    title="Confirm you have read this"
                                    onClick={() => handleAck(comment)}
                                >
                                    ACK
                                </Button>
                            )}
                            {renderAckMatrix(comment)}
                        </div>
                    )}
                    {comment.awaitingUser && !isResolved && (
                        <div className={classes.awaitingLine} title="This comment is waiting on someone.">
                            waiting on {comment.awaitingUser.fullName}
                        </div>
                    )}
                    {isResolved && (
                        <div className={classes.resolvedLine} title={comment.resolvedAt ? new Date(comment.resolvedAt).toLocaleString() : undefined}>
                            resolved by {comment.resolvedBy?.fullName || "someone"}
                        </div>
                    )}
                    {(comment.seenBy ?? 0) > 0 && (
                        <div className={classes.seenLine} title="How many people have opened this thread since it was posted.">
                            seen by {comment.seenBy}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={classes.commentSection}>
            <div className={classes.commentHeader}>
                <span className={classes.commentTitle}>
                    <i className="bi bi-chat-dots me-2" />
                    Comments {total > 0 && `(${total})`}
                </span>
                {parentId > 0 && (
                    <Button
                        type="button"
                        size="xs"
                        onClick={handleWatch}
                        title={watching ? "You get notified about new comments here. Click to stop." : "Get notified about new comments here."}
                    >
                        <i className={`bi ${watching ? "bi-bell-fill" : "bi-bell"}`} />
                        {watching ? "Watching ✓" : "Watch"}
                    </Button>
                )}
            </div>

            {parentId > 0 && total > 0 && (
                <AiResultCard title="Catch up" makeLabel="Catch me up" makeTitle="Summarise this thread with AI: what happened, decisions, open questions and who is waiting (names are hidden from the AI)"
                    getUrl={`ai/features/comments/${parentType}/${parentId}`} postUrl={`ai/features/comments/${parentType}/${parentId}`}
                    isAllowed={(access) => !!access.dataRules?.commentsFreeText} note="AI summary — check the comments for anything important" />
            )}

            {decisions.length > 0 && (
                <div className={classes.decisionsCard}>
                    <button type="button" className={`btn-unstyled ${classes.decisionsHead}`} aria-expanded={showDecisions}
                        onClick={() => setShowDecisions((open) => !open)} title="What was decided in this thread, newest first">
                        <i className="bi bi-patch-check-fill" aria-hidden="true" /> Decisions ({decisions.length})
                        <i className={`bi ${showDecisions ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true" />
                    </button>
                    {showDecisions && (
                        <ul className={classes.decisionsList}>
                            {decisions.map((decision) => (
                                <li key={decision.commentId}>
                                    <button type="button" className="btn-unstyled" onClick={() => scrollToComment(decision.commentId)}
                                        title="Show this comment in the thread (load older comments if it's further up)">
                                        {decision.content.length > 160 ? `${decision.content.slice(0, 159)}…` : decision.content}
                                    </button>
                                    <span className={classes.decisionMeta}>
                                        {[decision.decidedBy, decision.decidedAt ? new Date(decision.decidedAt).toLocaleDateString([], { day: "numeric", month: "short" }) : null].filter(Boolean).join(" · ")}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {pinned && (
                <div className={classes.pinnedCard}>
                    <div className={classes.pinnedHeader}>
                        <span className={classes.pinnedLabel}>
                            <i className="bi bi-pin-angle-fill me-1" />
                            PINNED · {pinned.createdBy?.fullName || "Unknown"}
                        </span>
                        <div className={classes.pinnedActions}>
                            <Button type="button" size="xs" icon="arrow-down-short" title="Scroll to the pinned comment" onClick={() => scrollToComment(pinned.commentId)}>
                                Show
                            </Button>
                            <Button type="button" size="xs" icon="pin-angle-fill" disabled={busyId === pinned.commentId} title="Remove this comment from the top of the thread" onClick={() => handlePin(pinned, false)}>
                                Unpin
                            </Button>
                        </div>
                    </div>
                    <div className={classes.pinnedBody}>
                        {renderCommentContent(pinned.content, { mentionClassName: classes.mentionTag, referenceClassName: classes.referenceLink })}
                    </div>
                </div>
            )}

            <div className={classes.commentList} ref={listRef}>
                {hasOlder && (
                    <div className={classes.loadMore}>
                        <Button type="button" size="xs" icon="arrow-up-circle" onClick={loadOlder} disabled={isLoading} title="Show earlier comments in this thread">
                            {isLoading ? "Loading..." : "Load older comments"}
                        </Button>
                    </div>
                )}
                {comments.length === 0 && !isLoading && (
                    <div className={classes.emptyState}>No comments yet</div>
                )}
                {rows.map((row) => {
                    if (row.kind === "day") {
                        return <div key={row.key} className={classes.daySeparator}><span>{row.label}</span></div>;
                    }
                    if (row.kind === "new") {
                        return <div key={row.key} className={classes.newSeparator}><span>New since your last visit · {newSinceCount}</span></div>;
                    }
                    return renderComment(row.comment, row.showHeader);
                })}
                <div ref={bottomRef} />
            </div>

            {readOnly && (
                <div className={classes.readOnlyBanner} data-testid="thread-read-only" title="Comments stay readable after a mission closes; nothing new can be posted.">
                    <i className="bi bi-archive me-1" />
                    Mission closed — this thread is archived (read-only)
                </div>
            )}

            {parentId > 0 && !readOnly && (
                <div className={classes.composer}>
                    {replyTo && (
                        <div className={classes.quoteStrip}>
                            <span className={classes.quoteStripText}>
                                Replying to <strong>{replyTo.createdBy?.fullName || "Unknown"}</strong>: {excerptOf(replyTo.content)}
                            </span>
                            <Button type="button" size="xs" icon="x-lg" title="Post without quoting" onClick={() => setReplyTo(null)}>
                                Cancel reply
                            </Button>
                        </div>
                    )}
                    <div className={classes.quickReplies} data-testid="quick-replies" role="group" aria-label="Quick replies">
                        {(MISSION_FAMILY_TYPES.has(parentType) ? MISSION_QUICK_REPLIES : OFFICE_QUICK_REPLIES).map((text) => (
                            <Button
                                key={text}
                                type="button"
                                size="xs"
                                disabled={isSubmitting}
                                title={`Post "${text}" now${replyTo ? " as a reply" : ""} — one tap, no typing`}
                                onClick={() => submitContent(text)}
                            >
                                {text}
                            </Button>
                        ))}
                    </div>
                    <div className={classes.newCommentForm}>
                        <CommentInput
                            value={newComment}
                            onChange={setNewComment}
                            onSubmit={handleSubmit}
                            disabled={isSubmitting}
                            mentionScope={{ parentType, parentId }}
                        />
                        <Button
                            type="button"
                            variant="primary"
                            icon="send"
                            onClick={handleSubmit}
                            disabled={!newComment.trim()}
                            loading={isSubmitting}
                            loadingText="Posting…"
                            title="Post this comment (Enter)"
                        >
                            Post
                        </Button>
                    </div>
                    <div className={classes.composerFooter}>
                        <div className={classes.ackToggle} title="Everyone notified must press ACK to confirm they read it.">
                            <Input
                                type="checkbox"
                                id={`requiresAck-${parentType}-${parentId}`}
                                label="Require ACK"
                                value={requiresAck}
                                onChange={(_name, value) => setRequiresAck(!!value)}
                            />
                        </div>
                        <span className={classes.recipientsHint} title="Who gets notified when you post.">{recipientsText}</span>
                    </div>
                </div>
            )}

            <TaskFormModal
                show={!!taskModal}
                onClose={() => setTaskModal(null)}
                onSaved={handleTaskSaved}
                parentType={taskModal?.parentType ?? null}
                parentId={taskModal?.parentId ?? null}
                initialData={taskModal?.initialData ?? null}
            />
            <ActivityFormModal
                show={!!activityModal}
                onClose={() => setActivityModal(null)}
                parentType={parentType}
                parentId={parentId}
                initial={activityModal?.initial ?? null}
                onSaved={handleActivitySaved}
            />
        </div>
    );
};

export default CommentSection;
